import type { AnalyticsProvider } from '~analytics/types/analyticsProvider';
import type { RuntimeConfig } from 'nuxt/schema';

/**
 * The operations our own code may call. `fbq` is one global queue and `track` dispatches to every
 * pixel initialised in it, so what keeps a third party's pixel out of our events is the pixel's
 * own `trackSingleOnly`, set where that pixel is initialised, rather than the shape of each send.
 * `fbevents.js` broadcasts on its own too, on every history change, and the same flag covers that.
 * @nuxt/scripts' ambient `Window.fbq` type owns the full real-world surface.
 */
export interface Fbq {
  (operationType: 'init', pixelId: string): void;
  (operationType: 'set', setting: 'autoConfig' | 'trackSingleOnly', enabled: boolean, pixelId: string): void;
  (operationType: 'track', eventName: string, eventData?: object): void;
  (operationType: 'trackSingle', pixelId: string, eventName: string, eventData?: object): void;
}

interface PixelAnalytics {
  proxy: {
    fbq: Fbq;
  };
}

export default class MetaPixelProvider implements AnalyticsProvider {
  serviceName: string;
  subscribedEvents: string[];
  config: RuntimeConfig;
  pixelAnalytics?: PixelAnalytics;
  mappedEvents: Record<string, string>

  constructor(
    serviceName: string,
    config: RuntimeConfig,
    subscribedEvents: string[],
  ) {
    this.serviceName = serviceName;
    this.subscribedEvents = subscribedEvents;
    this.config = config;
    this.mappedEvents = {
      'PHOENIX_ACCOUNT_REGISTER_COMPLETED': 'CompleteRegistration',
      'PHOENIX_LEARN_TOPIC_COMPLETED': 'SubmitApplication',
      'unified.content.story.completed': 'Lead',
    };
  }

  async init() {
    const pixelId = this.config.public.scripts.metaPixel?.id;
    if (!pixelId) {
      console.warn('Meta Pixel initialized without a Pixel ID');
      return;
    }

    // Load the Meta Pixel SDK - useScript handles CSP nonces automatically
    const { proxy } = <{ proxy: PixelAnalytics['proxy'] }>(<unknown>useScriptMetaPixel({ id: pixelId }));
    this.pixelAnalytics = { proxy };
  }

  track(eventName: string, eventData?: object) {
    if (!this.pixelAnalytics) {
      return;
    }

    const event = this.mappedEvents[<keyof typeof this.mappedEvents>eventName]
      ? this.mappedEvents[<keyof typeof this.mappedEvents>eventName]
      : eventName;

    this.pixelAnalytics.proxy.fbq('track', event ?? eventName, eventData);
  }

  identify() {
    // noop atm
  }

  reset() {
    // noop atm
  }

  // eslint-disable-next-line unused-imports/no-unused-vars
  setUserProperties(properties: Record<string, unknown>) {
    // noop atm
  }

  getSubscribedEvents() {
    return this.subscribedEvents;
  }
}
